import { motion } from "framer-motion";
import { IMG } from "@/data/site";

export default function About() {
  return (
    <section
      id="about"
      className="relative bg-[var(--pam-bg)] overflow-hidden"
      style={{ padding: "5% 0" }}
      data-testid="about-section"
    >
      {/* soft decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[var(--pam-gold)]/10 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[var(--pam-forest-2)]/10 blur-3xl"
      />

      <div className="container-pam grid md:grid-cols-12 gap-12 md:gap-16 items-center relative">
        {/* IMAGES — pop + rotation Framer entrance, staggered */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.4 }}
          className="md:col-span-6 relative h-[560px] sm:h-[640px] md:h-[720px]"
        >
          {/* main image — tall portrait, top-left */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: 45 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 1.15,
              delay: 0.1,
              ease: [0.34, 1.56, 0.64, 1], // springy cubic-bezier
            }}
            style={{ willChange: "transform" }}
            className="absolute top-0 left-0 w-[70%] h-[70%] rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-black/5"
          >
            <img
              src={IMG.aboutAmbience}
              alt="Peak Adventures Malnad — signature red-brick cottage in the coffee estate"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </motion.div>
          {/* secondary image — landscape, bottom-right, slightly overlapping */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: 45 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 1.15,
              delay: 0.35,
              ease: [0.34, 1.56, 0.64, 1],
            }}
            style={{ willChange: "transform" }}
            className="absolute bottom-0 right-0 w-[68%] h-[55%] rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-black/5"
          >
            <img
              src={IMG.aboutEstate}
              alt="Coffee-plantation walkway inside the estate"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </motion.div>
          {/* gold "collaboration" chip */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="absolute top-4 right-4 md:top-6 md:right-6 hidden md:flex items-center gap-2 bg-white text-[var(--pam-forest)] px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] shadow-lg ring-1 ring-black/5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--pam-gold)]" />
            Trusted homestay network
          </motion.div>
          {/* small stat card overlapping the two images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="absolute left-[36%] top-[54%] -translate-y-1/2 hidden md:block bg-[var(--pam-forest)] text-white px-5 py-4 rounded-2xl shadow-2xl"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--pam-gold)]">
              4 destinations
            </div>
            <div className="mt-1 font-serif text-lg leading-tight">
              Kalasa · Chikkamagaluru
              <br />
              Mudigere · Sakleshpur
            </div>
          </motion.div>
        </motion.div>

        {/* TEXT */}
        <div className="md:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="overline gold-text"
          >
            About us
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--pam-forest)]"
          >
            Where the Western Ghats
            <span className="italic text-[var(--pam-coffee)]">
              {" "}welcome you home.
            </span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 space-y-5 text-base sm:text-lg text-[var(--pam-muted)] leading-relaxed max-w-2xl"
          >
            <p>
              <span className="font-medium text-[var(--pam-forest)]">
                Peak Adventures Malnad
              </span>{" "}
              curates authentic Malnad experiences in{" "}
              <Hi>collaboration with hand-picked homestays</Hi> across{" "}
              <Hi>Kalasa, Chikkamagaluru, Mudigere & Sakleshpur</Hi>. Every
              partner stay is a real Malnad home — clay-tiled roofs, wide
              verandahs, hand-oiled wood and forest-facing windows — hosted by
              families who have lived on these hills for generations.
            </p>
            <p>
              We take the guesswork out of a Malnad getaway. From{" "}
              <Hi>couple cottages</Hi> and warm{" "}
              <Hi>family stays</Hi> to{" "}
              <Hi>group cottages</Hi> and a spirited{" "}
              <Hi>dormitory</Hi> for friends, our partners open their homes to
              guests who are looking for something more soulful than a hotel.
            </p>
            <p>
              Days begin with{" "}
              <Hi>estate-brewed filter coffee</Hi>, wander through coffee &amp;
              cardamom plantations, chase misty waterfalls, and close with a{" "}
              <Hi>slow bonfire</Hi> under a Ghat sky full of stars — a{" "}
              <Hi>traditional Malnad thali</Hi> waiting at every table.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 hidden"
            aria-hidden="true"
          >
            {/* Feature pills moved to global horizontal marquee above the About section */}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Hi({ children }) {
  return (
    <span className="relative inline text-[var(--pam-forest)] font-medium">
      <span className="relative z-10">{children}</span>
      <span
        aria-hidden
        className="absolute left-0 right-0 bottom-0 h-[6px] bg-[var(--pam-gold)]/35 rounded-sm -z-0"
      />
    </span>
  );
}
